import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Play, PauseCircle, XCircle, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

const TONE = { entwurf: "gray", aktiv: "green", ausgesetzt: "amber", gekuendigt: "red" };

export default function Contracts() {
  const [items, setItems] = useState(null);
  const [assets, setAssets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer: "", title: "", start_date: "", end_date: "", reaktionszeit_h: 24, interval_months: 12, leistungsumfang: "", asset_ids: [] });

  const load = useCallback(() => { api.get("/contracts").then((r) => setItems(r.data)); api.get("/assets").then((r) => setAssets(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await api.post("/contracts", { ...form, reaktionszeit_h: Number(form.reaktionszeit_h) || 24,
        interval_months: Number(form.interval_months) || 12,
        leistungsumfang: form.leistungsumfang.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Vertrag angelegt"); setOpen(false);
      setForm({ customer: "", title: "", start_date: "", end_date: "", reaktionszeit_h: 24, interval_months: 12, leistungsumfang: "", asset_ids: [] });
      load();
    } catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
  };
  const act = async (id, action) => { const { data } = await api.post(`/contracts/${id}/${action}`); toast.success(action === "activate" ? `Aktiviert · ${data.inherited_assets} Anlagen vererbt` : "Aktualisiert"); load(); };
  const toggleAsset = (id) => setForm((f) => ({ ...f, asset_ids: f.asset_ids.includes(id) ? f.asset_ids.filter((x) => x !== id) : [...f.asset_ids, id] }));

  if (!items) return <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-md" />)}</div>;

  return (
    <div>
      <PageHeader title="Wartungsverträge" subtitle="Leistungsumfang · SLA · Erfüllung · Vererbung auf Anlagen">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-contract-button" className="gap-2"><Plus size={18} weight="bold" />Neuer Vertrag</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="contract-dialog">
            <DialogHeader><DialogTitle className="font-heading">Wartungsvertrag anlegen</DialogTitle></DialogHeader>
            <DialogDescription>Leistungsumfang, SLA und abgedeckte Anlagen.</DialogDescription>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1.5"><Label2>Kunde *</Label2><Input data-testid="contract-customer" value={form.customer} onChange={(e) => setForm((f) => ({ ...f, customer: e.target.value }))} /></div>
              <div className="col-span-2 space-y-1.5"><Label2>Titel *</Label2><Input data-testid="contract-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label2>Start</Label2><Input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label2>Ende</Label2><Input type="date" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label2>Reaktionszeit (h)</Label2><Input type="number" value={form.reaktionszeit_h} onChange={(e) => setForm((f) => ({ ...f, reaktionszeit_h: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label2>Intervall (Monate)</Label2><Input type="number" value={form.interval_months} onChange={(e) => setForm((f) => ({ ...f, interval_months: e.target.value }))} /></div>
              <div className="col-span-2 space-y-1.5"><Label2>Leistungsumfang (kommagetrennt)</Label2><Input value={form.leistungsumfang} onChange={(e) => setForm((f) => ({ ...f, leistungsumfang: e.target.value }))} placeholder="Wartung, Prüfung, Notdienst" /></div>
              <div className="col-span-2 space-y-1.5"><Label2>Abgedeckte Anlagen</Label2>
                <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-border p-2">
                  {assets.map((a) => (
                    <label key={a.id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.asset_ids.includes(a.id)} onChange={() => toggleAsset(a.id)} />{a.name}</label>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter><Button data-testid="contract-save" onClick={submit} disabled={!form.customer || !form.title}>Anlegen</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="space-y-4">
        {items.length === 0 && <Card className="rounded-md border p-8 text-center text-sm text-muted-foreground">Keine Verträge.</Card>}
        {items.map((c) => (
          <Card key={c.id} data-testid="contract-row" className="rounded-md border p-5">
            <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
              <div>
                <div className="flex items-center gap-2"><span className="font-semibold">{c.title}</span><span className="font-mono text-xs text-primary">{c.contract_number}</span></div>
                <div className="text-sm text-muted-foreground">{c.customer} · SLA {c.reaktionszeit_h}h · Intervall {c.interval_months}M · {c.covered_assets} Anlagen</div>
                <div className="mt-1 text-xs text-muted-foreground">{(c.leistungsumfang || []).join(" · ")}</div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {c.expiring && <StatusBadge tone="amber">läuft aus</StatusBadge>}
                <StatusBadge tone={TONE[c.status]}>{c.status}</StatusBadge>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {c.status !== "aktiv" && c.status !== "gekuendigt" && <Button size="sm" className="gap-1.5" data-testid={`activate-contract-${c.id}`} onClick={() => act(c.id, "activate")}><Play size={15} />Aktivieren (Vererbung)</Button>}
              {c.status === "aktiv" && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => act(c.id, "suspend")}><PauseCircle size={15} />Aussetzen</Button>}
              {c.status !== "gekuendigt" && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => act(c.id, "terminate")}><XCircle size={15} />Kündigen</Button>}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
